import { useState } from 'react'
import { Link } from '@inertiajs/react'
import type { MarketingLayoutProps } from '@/types/marketing'
import CookieConsentBanner from './CookieConsentBanner'

// Inertia's <Link> only works for routes served by the marketing entrypoint.
// Portal / admin / enterprise pages live on different Inertia entrypoints, so
// we need a hard <a href> to load them — otherwise the resolver throws when
// the response component name isn't registered.
const NON_MARKETING_PATH_PREFIXES = ['/log-in', '/login', '/dashboard', '/admin', '/e/', '/portal']

function FooterLink({ href, label }: { href: string; label: string }) {
  const isHardLink =
    href.startsWith('mailto:') ||
    href.startsWith('#') ||
    href.startsWith('http') ||
    href.includes('/apply') ||
    NON_MARKETING_PATH_PREFIXES.some((prefix) => href.startsWith(prefix))

  if (isHardLink) {
    return (
      <a href={href} className="marketing-footer-link">
        {label}
      </a>
    )
  }

  return (
    <Link href={href} className="marketing-footer-link" prefetch>
      {label}
    </Link>
  )
}

function RegionalPill({ href, label }: { href: string; label: string }) {
  return (
    <Link
      href={href}
      className="marketing-chip"
      prefetch
    >
      {label}
    </Link>
  )
}

const socialLinks = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/wavbee',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
      </svg>
    )
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@wavbee',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.75a8.18 8.18 0 0 0 4.76 1.52V6.84a4.84 4.84 0 0 1-1-.15z"/>
      </svg>
    )
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/wavbee',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    )
  },
  {
    label: 'X',
    href: 'https://x.com/wavbee',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    )
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/wavbee',
    icon: (
      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    )
  }
]

export default function MarketingFooter({ layout }: { layout: MarketingLayoutProps }) {
  const [cookiePrefsOpen, setCookiePrefsOpen] = useState(false)

  return (
    <footer className="marketing-footer">
      {/* Gradient top accent line */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#ff6a1a]/30 to-transparent" />

      <div className="container mx-auto px-6 py-14">
        {/* Brand + Link columns */}
        <div className="grid gap-12 lg:grid-cols-[1fr_2fr]">
          {/* Brand column */}
          <div>
            <img src={layout.logo_url} alt="wavbee" className="h-7 w-auto" />
            <p className="mt-4 max-w-[18rem] text-[0.8125rem] leading-[1.75] text-white/40">
              {layout.footer.tagline}
            </p>
            <div className="mt-6 flex items-center gap-2.5">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2 text-white/25 transition-all hover:border-white/[0.12] hover:text-[#ff6a1a]"
                  aria-label={social.label}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link grid — 4 columns on md+, 2 columns on mobile */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-4">
            {layout.footer.groups.map((group) => (
              <div key={group.title}>
                <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-white/30">
                  {group.title}
                </h2>
                <div className="mt-4 flex flex-col gap-2.5">
                  {group.links.map((link) => (
                    <FooterLink key={`${group.title}-${link.href}`} href={link.href} label={link.label} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Regional row */}
        {layout.footer.regional_links && layout.footer.regional_links.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-white/[0.06] pt-8">
            <span className="mr-1 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-white/30">
              Available in
            </span>
            {layout.footer.regional_links.map((link) => (
              <RegionalPill key={link.href} href={link.href} label={link.label} />
            ))}
          </div>
        )}

        {/* Bottom bar */}
        <div className="mt-8 flex flex-col gap-3 border-t border-white/[0.06] pt-6 text-[0.8125rem] text-white/28 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p>&copy; {new Date().getFullYear()} WAVBEE TECHNOLOGIES. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {layout.footer.legal_links.map((link) => (
              <FooterLink key={link.href} href={link.href} label={link.label} />
            ))}
            <button
              type="button"
              onClick={() => setCookiePrefsOpen(true)}
              className="marketing-footer-link"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
      {cookiePrefsOpen && <CookieConsentBanner alwaysOpen onClose={() => setCookiePrefsOpen(false)} />}
    </footer>
  )
}
